import './styles.scss';
import { useContext, useEffect, useState, useMemo } from 'react';

import { BackgroundMessage } from '../../components/background-message';
import { PNLContext } from '../../providers/pnl-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../providers/portfolio-management';
import { PositionsContext } from '../../providers/positions/positions-provider';
import { RiskManagementContext } from '../../providers/risk-management/risk-management-provider';
import { StrikeReportContext } from '../../providers/strike-report';
import { TransactionsContext } from '../../providers/transactions/transactions-provider';
import { IAccountOption, VenueContext } from '../../providers/venue-provider';

import { PortfolioHeader } from './portfolio-header';
import { PortfolioSidebar } from './portfolio-sidebar';
import { PortfolioSidebarSettings } from './portfolio-sidebar-settings';
import { PortfolioTabs } from './portfolio-tabs';
import { PortfolioMessageHandler } from '../shared/portfoilo-message-handler';

const PortfolioPerAccountContent = ({
  isPortfolioSidebarOpen,
  selectedAccountOption,
  credentials,
}: {
  isPortfolioSidebarOpen: boolean;
  selectedAccountOption: IAccountOption;
  credentials: any;
}) => {
  const content = useMemo(() => {
    return credentials?.map((credential: any, index: any) =>
      credential.items.map(
        (item: any) =>
          item.name === selectedAccountOption.name && (
            <div
              key={`${item.name}-${index}`}
              className={`portfolio-management-content ${
                isPortfolioSidebarOpen ? 'portfolio-sidebar-open' : 'portfolio-sidebar-close'
              }`}
            >
              <PortfolioHeader />
              <div className="portfolio-management-content-tabs">
                <PortfolioTabs accountOption={selectedAccountOption} />
              </div>
            </div>
          ),
      ),
    );
  }, [credentials, isPortfolioSidebarOpen, selectedAccountOption]);

  return <>{content}</>;
};

const PortfolioAggregatedContent = ({
  isPortfolioSidebarOpen,
  selectedAccountOption,
}: {
  isPortfolioSidebarOpen: boolean;
  selectedAccountOption: IAccountOption;
}) => {
  const content = useMemo(() => {
    return (
      <div
        className={`portfolio-management-content ${
          isPortfolioSidebarOpen ? 'portfolio-sidebar-open' : 'portfolio-sidebar-close'
        }`}
      >
        <PortfolioHeader isAggregatedAccounts />
        <div className="portfolio-management-content-tabs">
          <PortfolioTabs accountOption={selectedAccountOption} />
        </div>
      </div>
    );
  }, [isPortfolioSidebarOpen, selectedAccountOption]);

  return <>{content}</>;
};

const PortfolioNoContent = ({ isPortfolioSidebarOpen }: { isPortfolioSidebarOpen: boolean }) => (
  <div
    className={`portfolio-management-content ${
      isPortfolioSidebarOpen ? 'portfolio-sidebar-open' : 'portfolio-sidebar-close'
    }`}
  >
    <div className="portfolio-no-content">
      <PortfolioMessageHandler />
    </div>
  </div>
);

const PortfolioManagement = () => {
  const [isPortfolioSidebarOpen, setIsPortfolioSidebarOpen] = useState(true);
  const {
    selectedAccountOption,
    selectedDataDisplayOption,
    setIsPortfolioLoading,
    aggregatedCalculationStatus,
    isPortfolioSidebarSettingsOpen,
  } = useContext(PortfolioManagementContext);
  const { credentials, isLoading: isCredentialsLoading } = useContext(VenueContext);
  const { isRiskAllSpotLoading } = useContext(RiskManagementContext);
  const { isLoadingPnl } = useContext(PNLContext);
  const { isLoadingPositions, isLoadingOrders } = useContext(PositionsContext);
  const { isLoadingTransactions } = useContext(TransactionsContext);
  const { isStrikeReportLoading } = useContext(StrikeReportContext);

  const isCredentialsEmpty = !isCredentialsLoading && credentials.length === 0;
  const isPortfolioAllLoading =
    isRiskAllSpotLoading ||
    isLoadingPnl ||
    isLoadingPositions ||
    isLoadingOrders ||
    isLoadingTransactions ||
    isStrikeReportLoading;

  useEffect(() => {
    setIsPortfolioLoading(isPortfolioAllLoading);
  }, [isPortfolioAllLoading, setIsPortfolioLoading]);

  if (isCredentialsLoading) {
    return (
      <div className="portfolio-management-credentials-loading-container">
        <BackgroundMessage isLoading />
      </div>
    );
  }

  if (isCredentialsEmpty) {
    return (
      <div className="portfolio-management-container">
        <PortfolioMessageHandler emptyCredentials />
      </div>
    );
  }

  const hasAnyAccountOptionSelected = Object.keys(selectedAccountOption).length > 0;

  return (
    <div className="portfolio-management-container">
      <PortfolioSidebar
        isPortfolioSidebarOpen={isPortfolioSidebarOpen}
        setIsPortfolioSidebarOpen={setIsPortfolioSidebarOpen}
      />
      {isPortfolioSidebarSettingsOpen && <PortfolioSidebarSettings />}
      {selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount ? (
        hasAnyAccountOptionSelected ? (
          <PortfolioPerAccountContent
            isPortfolioSidebarOpen={isPortfolioSidebarOpen}
            selectedAccountOption={selectedAccountOption}
            credentials={credentials}
          />
        ) : (
          <PortfolioNoContent isPortfolioSidebarOpen={isPortfolioSidebarOpen} />
        )
      ) : aggregatedCalculationStatus ? (
        <PortfolioAggregatedContent
          isPortfolioSidebarOpen={isPortfolioSidebarOpen}
          selectedAccountOption={selectedAccountOption}
        />
      ) : (
        <PortfolioNoContent isPortfolioSidebarOpen={isPortfolioSidebarOpen} />
      )}
    </div>
  );
};

export default PortfolioManagement;
